import { Navigate, useNavigate } from 'react-router-dom';

import { DateAdapter } from '@myphx-engineering/date-utils/client';

import PlanSummary from '../../components/plan-summary/PlanSummary';
import { getFragment } from '../../graphql';
import {
    type CoverageInfoFragment,
    GetPatientDocument,
} from '../../graphql/generated/graphql';
import { useFlags } from '../../hooks/use-flags';
import { usePatientContext } from '../../hooks/use-patient-context';
import { QueryLoader } from '../../loaders';
import type { ResultOf } from '../../types';
import { getAbsoluteRoute } from '../../util';

import PlanSummaryRouteV2 from './PlanSummaryRouteV2';

const PlanSummaryRoute = () => {
    const Loader = QueryLoader<typeof GetPatientDocument>;
    const { primaryPatientId: patientId } = usePatientContext();
    const { memberClaimHistoryEnabled } = useFlags();

    if (memberClaimHistoryEnabled) {
        return <PlanSummaryRouteV2 />;
    }

    return (
        <Loader
            query={GetPatientDocument}
            component={DataHandler}
            variables={{ patientId }}
        />
    );
};

type DataHandlerProps = { data: ResultOf<typeof GetPatientDocument> };

const DataHandler = ({ data }: DataHandlerProps) => {
    const navigate = useNavigate();
    const coverageEdges: CoverageInfoFragment[] = [];
    const patient = getFragment(data.patient);
    let planDetailsUrl;
    patient?.coveragesConnection.edges?.forEach((edge) => {
        const coverage = getFragment(edge.coverage);
        if (DateAdapter.isInDateRange(coverage.startDate, coverage.endDate)) {
            coverageEdges.push(coverage);
            const plan = getFragment(edge.node);
            if (plan.__typename == 'PhxBenefitsPlan' && plan.planDetails) {
                planDetailsUrl = plan.planDetails;
            }
        }
    });

    if (!coverageEdges) {
        return <Navigate to="/" />;
    }

    return (
        <PlanSummary
            coverageEdges={coverageEdges}
            onClaimHistoryClick={() =>
                navigate(getAbsoluteRoute('claims.history'))
            }
            planDetailsUrl={planDetailsUrl}
        />
    );
};

export default PlanSummaryRoute;
